/**
 * Permission evaluation for RENDERING.
 *
 * ===========================================================================
 * READ THIS BEFORE USING ANY OF IT
 * ===========================================================================
 * Nothing in this file is a security control. It decides what to DRAW.
 *
 * The server re-checks every capability and every resource scope on execution, in
 * `kernel/authorization/policy.py`, and it is the only thing standing between a user
 * and a record. A `useCan()` that returns true does not mean the action will succeed;
 * a `useCan()` that returns false does not stop anyone. Hiding a button is a courtesy
 * to the user, not a defence — someone with the console open can call the endpoint,
 * and the endpoint will refuse them.
 *
 * The consequence that matters in review: NEVER let a UI check be the only check for
 * anything. If you find yourself thinking "the button is hidden so we don't need the
 * server to validate this", stop. That is the bug this comment exists to prevent.
 * ===========================================================================
 *
 * The model, from `kernel/authorization/catalogue.py`:
 *
 *   Permissions are `module.action.scope`, e.g. `case.transition.assigned`.
 *   Scopes widen additively, narrowest to widest:
 *     own < assigned < team < department < organization < platform
 *   A role holding two gets the union. There are NO negative grants.
 *
 * So "may this user transition cases at all?" is: do they hold `case.transition.*`
 * at ANY scope? That is `can(permissions, 'case', 'transition')`.
 *
 * Whether they may transition THIS case is a different question, and the frontend
 * cannot answer it — scope resolution needs case-team membership, department
 * membership and per-matter grants. The server answers it, per record, in the
 * `_actions` block on every detail payload and in `available_transitions`. Use those
 * for a specific record; use `can()` only for navigation and for chrome.
 */

import type { PermissionAction, PermissionKey, PermissionModule, PermissionScope } from './types';

export const SCOPE_ORDER: readonly PermissionScope[] = [
  'own',
  'assigned',
  'team',
  'department',
  'organization',
  'platform',
];

export interface ParsedPermission {
  module: string;
  action: string;
  scope: PermissionScope;
}

export function parsePermission(key: PermissionKey): ParsedPermission | null {
  const parts = key.split('.');
  if (parts.length !== 3) return null;
  const [module, action, scope] = parts as [string, string, PermissionScope];
  if (!SCOPE_ORDER.includes(scope)) return null;
  return { module, action, scope };
}

/**
 * A permission set with the lookups the UI actually needs.
 *
 * Built once per `me` response and memoised. Parsing 200 strings on every render of
 * every nav item is measurable, and the set only changes when the session does.
 */
export class PermissionSet {
  /** Every raw key, for debugging and for the developer tools panel. */
  readonly keys: readonly PermissionKey[];
  /** `module.action` -> the widest scope held. */
  private readonly widest: Map<string, PermissionScope>;

  constructor(keys: readonly PermissionKey[]) {
    this.keys = keys;
    this.widest = new Map();
    for (const key of keys) {
      const parsed = parsePermission(key);
      if (!parsed) continue;
      const capability = `${parsed.module}.${parsed.action}`;
      const current = this.widest.get(capability);
      if (!current || SCOPE_ORDER.indexOf(parsed.scope) > SCOPE_ORDER.indexOf(current)) {
        this.widest.set(capability, parsed.scope);
      }
    }
  }

  /** Does this actor hold `module.action` at any scope? */
  has(module: PermissionModule | string, action: PermissionAction | string): boolean {
    return this.widest.has(`${module}.${action}`);
  }

  /**
   * Does this actor hold `module.action` at `scope` OR WIDER?
   *
   * Careful with this one. "Holds `case.view` at department scope or wider" is a
   * reasonable question for deciding whether to show a firm-wide filter. It is NOT
   * a way to decide whether they may see a particular matter.
   */
  hasAtLeast(
    module: PermissionModule | string,
    action: PermissionAction | string,
    scope: PermissionScope,
  ): boolean {
    const held = this.widest.get(`${module}.${action}`);
    if (!held) return false;
    return SCOPE_ORDER.indexOf(held) >= SCOPE_ORDER.indexOf(scope);
  }

  /** The widest scope held for a capability, or null. */
  scopeFor(
    module: PermissionModule | string,
    action: PermissionAction | string,
  ): PermissionScope | null {
    return this.widest.get(`${module}.${action}`) ?? null;
  }

  /** True if ANY of the capabilities is held. Use for "should this nav section exist". */
  hasAny(capabilities: Array<[string, string]>): boolean {
    return capabilities.some(([module, action]) => this.has(module, action));
  }

  /** True only if EVERY capability is held. */
  hasAll(capabilities: Array<[string, string]>): boolean {
    return capabilities.every(([module, action]) => this.has(module, action));
  }
}

export const EMPTY_PERMISSIONS = new PermissionSet([]);

/**
 * Read an action off a server-supplied `_actions` block.
 *
 * PREFER THIS over `can()` whenever you have a record in hand. `_actions` is computed
 * by the same kernel that will enforce the action, against this specific record, so it
 * is right where a capability check is only approximately right. It still is not a
 * control — the server re-checks on execution regardless.
 */
export function allows(
  actions: Record<string, boolean | undefined> | undefined,
  action: string,
): boolean {
  return actions?.[action] === true;
}
